import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useAuth } from '../context/AuthContext';
import { useCart } from '../context/CartContext';
import { ShoppingCart, Download, ExternalLink, Share2 } from 'lucide-react';
import api from '../utils/api';
import toast from 'react-hot-toast';
import { getImageUrl } from '../utils/imageUrl';
import SharePopup from '../components/SharePopup';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ProductDetail = () => {
  const { id } = useParams();
  const { user } = useAuth();
  const { addToCart } = useCart();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showSharePopup, setShowSharePopup] = useState(false);

  useEffect(() => {
    fetchProduct();
  }, [id]);

  const fetchProduct = async () => {
    try {
      const response = await api.get(`/products/${id}`);
      setProduct(response.data);
    } catch (error) {
      console.error('Failed to fetch product:', error);
      toast.error('Product not found');
    } finally {
      setLoading(false);
    }
  };

  const handleAddToCart = () => {
    if (!user) {
      toast.error('Please login to add to cart');
      return;
    }
    addToCart(product.id, 'product', 1);
  };

  // Share URL for social media with proper OG tags
  const shareUrl = `${BACKEND_URL}/api/share/product/${id}`;

  // Get product image for OG tags
  const getOgImage = () => {
    if (product?.images && product.images.length > 0) {
      const img = product.images[0];
      if (img.startsWith('http')) return img;
      return `${BACKEND_URL}${img}`;
    }
    return `${BACKEND_URL}/static/og-default.png`;
  };

  if (loading) {
    return (
      <div className="min-h-screen pt-24 pb-16 px-6 flex items-center justify-center">
        <div className="text-2xl">Loading...</div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="min-h-screen pt-24 pb-16 px-6 flex items-center justify-center">
        <div className="text-2xl">Product not found</div>
      </div>
    );
  }

  return (
    <>
      {/* OG Meta Tags for Social Sharing */}
      <Helmet>
        <title>{product?.name} | ANANT Web Portal</title>
        <meta name="description" content={product?.description?.substring(0, 160)} />
        
        {/* Open Graph / Facebook */}
        <meta property="og:type" content="product" />
        <meta property="og:url" content={`${window.location.origin}/product/${id}`} />
        <meta property="og:title" content={product?.name} />
        <meta property="og:description" content={product?.description?.substring(0, 160)} />
        <meta property="og:image" content={getOgImage()} />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:image:alt" content={product?.name} />
        <meta property="product:price:amount" content={product?.price} />
        <meta property="product:price:currency" content="INR" />
        
        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={product?.name} />
        <meta name="twitter:description" content={product?.description?.substring(0, 160)} />
        <meta name="twitter:image" content={getOgImage()} />
      </Helmet>
      
      <div className="min-h-screen pt-24 pb-16 px-6" data-testid="product-detail-page">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Product Images */}
            <div className="space-y-4">
              {product.images && product.images.length > 0 ? (
                <div className="aspect-square rounded-2xl overflow-hidden">
                  <img
                    src={getImageUrl(product.images[0])}
                    alt={product.name}
                    className="w-full h-full object-cover"
                    data-testid="product-image"
                  />
                </div>
              ) : (
                <div className="aspect-square rounded-2xl bg-white/5 flex items-center justify-center">
                  <span className="text-muted-foreground">No image available</span>
                </div>
              )}
            </div>

            {/* Product Info */}
            <div className="space-y-6">
              <div>
                <div className="flex items-center justify-between mb-4">
                  <span className="inline-block text-xs font-semibold text-secondary px-3 py-1 bg-secondary/20 rounded-full">
                    {product.category}
                  </span>
                  {/* Share Button */}
                  <button
                    onClick={() => setShowSharePopup(true)}
                    className="flex items-center gap-2 px-4 py-2 bg-green-500/20 text-green-500 rounded-xl hover:bg-green-500/30 transition-all"
                    data-testid="share-product-btn"
                  >
                    <Share2 className="w-4 h-4" />
                    Share
                  </button>
                </div>
                <h1 className="text-4xl md:text-5xl font-bold mb-4" data-testid="product-name">
                  {product.name}
                </h1>
              </div>

            <div className="flex items-center gap-4">
              {product.original_price && (
                <span className="text-2xl text-muted-foreground line-through">
                  ₹{product.original_price}
                </span>
              )}
              <span className="text-4xl font-bold text-primary" data-testid="product-price">
                ₹{product.price}
              </span>
              {product.original_price && (
                <span className="text-lg font-bold text-accent">
                  {Math.round(((product.original_price - product.price) / product.original_price) * 100)}% OFF
                </span>
              )}
            </div>

            <div className="prose prose-invert max-w-none">
              <p className="text-muted-foreground text-lg leading-relaxed" data-testid="product-description">
                {product.description}
              </p>
            </div>

            {/* Files and Links */}
            {product.files && product.files.length > 0 && (
              <div className="space-y-2">
                <h3 className="text-lg font-semibold">Included Files:</h3>
                <div className="space-y-2">
                  {product.files.map((file, index) => (
                    <div key={index} className="flex items-center gap-2 text-muted-foreground">
                      <Download className="w-4 h-4" />
                      <span>{file.name}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {product.external_links && product.external_links.length > 0 && (
              <div className="space-y-2">
                <h3 className="text-lg font-semibold">External Links:</h3>
                <div className="space-y-2">
                  {product.external_links.map((link, index) => (
                    <div key={index} className="flex items-center gap-2 text-muted-foreground">
                      <ExternalLink className="w-4 h-4" />
                      <span>{link.name}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <button
              onClick={handleAddToCart}
              className="w-full btn-primary flex items-center justify-center gap-2"
              data-testid="add-to-cart-button"
            >
              <ShoppingCart className="w-5 h-5" />
              Add to Cart
            </button>
          </div>
        </div>
      </div>
    </div>
    
    {/* Share Popup */}
    <SharePopup 
      isOpen={showSharePopup}
      onClose={() => setShowSharePopup(false)}
      shareUrl={shareUrl}
      title={product?.name || 'Product'}
      description={product?.description?.substring(0, 100)}
      itemType="product"
      itemId={id}
    />
    </>
  );
};

export default ProductDetail;
